import React, { useState, useEffect, useRef } from 'react';
import { Loader } from "components/Loader";
import { getServicesManager } from "services";
import { clearCache, SERVICE_CALLS } from 'extension-helpers';
import { AudioRecorder } from 'lib/recorder';
import { Typography } from 'interfaces/typography';
import { PrimaryActionButton, StandardContainer } from 'utils/CommonUIComponents';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { updateUser } from 'lib/redux/users/slice';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VoiceChat as VoiceChatIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';

type WorkflowStep = 'initial' | 'recording' | 'recorded' | 'hasGreeting';

export const VoicemailGreeting: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.value);
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>('initial');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const mediaRecorderRef = useRef<AudioRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Determine if user has an intake greeting based on user data
  const hasGreeting = user?.has_custom_intake_greeting === true;

  useEffect(() => {
    clearCache(SERVICE_CALLS.PARALLEL_SETTINGS)
    clearCache(SERVICE_CALLS.PARALLEL_VM_OPTIONS)
  }, []);

  useEffect(() => {
    // Set workflow step based on whether user has a greeting
    if (hasGreeting) {
      setWorkflowStep('hasGreeting');
    } else {
      setWorkflowStep('initial');
    }
  }, [hasGreeting]);

  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    setRecordingTime(0);
    setIsRecording(true);
    const recorder = mediaRecorderRef.current = new AudioRecorder()
    recorder.ready.then(() => setWorkflowStep('recording'))
    recorder.blob.then(audioBlob => {
      setAudioBlob(audioBlob);
      setWorkflowStep('recorded');
      setIsRecording(false);
    })
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.ensureStopped();
    setIsRecording(false);
  };

  const handleRecordNewGreeting = () => {
    if (workflowStep === 'initial' || workflowStep === 'hasGreeting') {
      startRecording();
    } else if (workflowStep === 'recording') {
      stopRecording();
    }
  };

  const handleSaveRecording = async () => {
    if (audioBlob) {
      try {
        const servicesManager = getServicesManager();

        // Convert the audioBlob to a File object and upload
        const file = new File([audioBlob], `intake-greeting.wav`, { type: audioBlob.type });
        const result = await servicesManager.uploadIntakeGreeting(file);

        if (result) {
          // If upload is successful, update Redux store immediately
          if (user) {
            dispatch(updateUser({
              ...user,
              has_custom_intake_greeting: true
            }));
          }
          clearCache(SERVICE_CALLS.PARALLEL_SETTINGS);
          setWorkflowStep('hasGreeting');
          setAudioBlob(null);
          setRecordingTime(0);
          setNotification({
            open: true,
            message: 'Voicemail greeting saved successfully!',
            severity: 'success'
          });
        } else {
          setNotification({
            open: true,
            message: 'Failed to save the voicemail greeting. Please try again.',
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error saving voicemail greeting:', error);
        setNotification({
          open: true,
          message: 'An error occurred while saving the voicemail greeting.',
          severity: 'error'
        });
      }
    }
  };

  const handleDiscardRecording = () => {
    setWorkflowStep(hasGreeting ? 'hasGreeting' : 'initial');
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const handleConfirmDelete = async () => {
    if (!hasGreeting) return;

    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      const success = await getServicesManager().deleteIntakeGreeting();
      if (success) {
        // Update Redux store immediately
        if (user) {
          dispatch(updateUser({
            ...user,
            has_custom_intake_greeting: false
          }));
        }
        clearCache(SERVICE_CALLS.PARALLEL_SETTINGS);
        setWorkflowStep('initial');
        setAudioURL(null);
        setNotification({
          open: true,
          message: 'Voicemail greeting deleted successfully!',
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: 'Failed to delete voicemail greeting. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting voicemail greeting:', error);
      setNotification({
        open: true,
        message: 'An error occurred while deleting the voicemail greeting.',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePlayGreeting = async () => {
    if (!hasGreeting || audioURL || isLoadingAudio) return;

    setIsLoadingAudio(true);
    try {
      const url = await getServicesManager().getIntakeGreetingURL();
      if (url) {
        setAudioURL(url);
      } else {
        console.error('Fetched data is not a url:', JSON.stringify(url));
        setNotification({
          open: true,
          message: 'Failed to load voicemail greeting audio.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching voicemail greeting audio:', error);
      setNotification({
        open: true,
        message: 'Failed to load voicemail greeting audio.',
        severity: 'error'
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };

  if (!user) return <Loader />;

  return (
    <StandardContainer maxWidth="max-w-5xl">
      {/* Header */}
      <section className="flex justify-between items-center">
        <div>
          <Typography variant="h3">Voicemail Greeting</Typography>
          <Typography variant="mediumParagraph" color="#6B7280">
            Set a custom voicemail greeting for when people call your number
          </Typography>
        </div>
      </section>

      {/* Main Content Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid rgb(229, 231, 235)',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {workflowStep === 'initial' && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <VoiceChatIcon sx={{ fontSize: 40, color: 'rgb(156, 163, 175)', mb: 1.5 }} />
              <div style={{ marginBottom: '4px' }}>
                <Typography variant="h5">No Voicemail Greeting Set</Typography>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="mediumParagraph" color="#6B7280">
                  Record a voicemail greeting that will play when people call your number
                </Typography>
              </div>
              <PrimaryActionButton
                icon={<MicIcon />}
                onClick={handleRecordNewGreeting}
                className="px-3 py-1.5 bg-red-500 hover:bg-red-600"
              >
                Start Recording
              </PrimaryActionButton>
            </Box>
          )}

          {workflowStep === 'recording' && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2
              }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: 'rgb(239, 68, 68)',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  },
                  animation: 'pulse 1s infinite'
                }} />
                <Typography variant="h5" color="#EF4444" fontWeight={500}>
                  Recording...
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                mb: 2
              }}>
                <AccessTimeIcon sx={{ fontSize: 20, color: 'rgb(107, 114, 128)' }} />
                <Typography variant="mediumParagraph" color="#6B7280" style={{ fontFamily: 'monospace' }}>
                  {formatTime(recordingTime)}
                </Typography>
              </Box>
              <PrimaryActionButton
                icon={<StopIcon />}
                onClick={handleRecordNewGreeting}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700"
              >
                Stop Recording
              </PrimaryActionButton>
            </Box>
          )}

          {workflowStep === 'recorded' && (
            <Box sx={{ py: 1 }}>
              <div style={{ marginBottom: '12px' }}>
                <Typography variant="h5">Review Recording</Typography>
              </div>
              {audioBlob && (
                <Box sx={{ mb: 2 }}>
                  <audio
                    src={URL.createObjectURL(audioBlob)}
                    controls
                    style={{ width: '100%', marginBottom: '12px' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryActionButton
                  icon={<SaveIcon />}
                  onClick={handleSaveRecording}
                  disabled={!audioBlob}
                  className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-300"
                >
                  Save Greeting
                </PrimaryActionButton>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleDiscardRecording}
                  sx={{
                    flex: 1,
                    borderRadius: '8px',
                    py: 1.5,
                    borderColor: 'rgb(229, 231, 235)',
                    color: 'rgb(75, 85, 99)',
                    '&:hover': {
                      borderColor: 'rgb(107, 114, 128)',
                      backgroundColor: alpha('rgb(0, 0, 0)', 0.04)
                    },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Discard
                </Button>
              </Box>
            </Box>
          )}

          {workflowStep === 'hasGreeting' && hasGreeting && (
            <Box sx={{ py: 1 }}>
              <div style={{ marginBottom: '12px' }}>
                <Typography variant="h5">Current Voicemail Greeting</Typography>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="mediumParagraph" color="#6B7280">
                  This greeting will play when people call your number and you don't answer
                </Typography>
              </div>

              {audioURL && (
                <Box sx={{ mb: 2 }}>
                  <audio
                    src={audioURL}
                    controls
                    style={{ width: '100%' }}
                  />
                </Box>
              )}

              {!audioURL && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={isLoadingAudio ? <CircularProgress size={16} /> : <PlayIcon />}
                    onClick={handlePlayGreeting}
                    disabled={isLoadingAudio}
                    sx={{
                      borderRadius: '8px',
                      borderColor: 'rgb(229, 231, 235)',
                      color: 'rgb(75, 85, 99)',
                      '&:hover': {
                        borderColor: 'rgb(107, 114, 128)',
                        backgroundColor: alpha('rgb(0, 0, 0)', 0.04)
                      },
                      '&:disabled': {
                        borderColor: 'rgb(229, 231, 235)',
                        color: 'rgb(156, 163, 175)'
                      },
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    {isLoadingAudio ? 'Loading...' : 'Play Greeting'}
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryActionButton
                  icon={<MicIcon />}
                  onClick={handleRecordNewGreeting}
                  className="flex-1 py-1.5 bg-blue-500 hover:bg-blue-600"
                >
                  Record New Greeting
                </PrimaryActionButton>
                <Button
                  variant="outlined"
                  startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  sx={{
                    flex: 1,
                    borderRadius: '8px',
                    py: 1.5,
                    borderColor: 'rgb(229, 231, 235)',
                    color: 'rgb(239, 68, 68)',
                    '&:hover': {
                      borderColor: 'rgb(239, 68, 68)',
                      backgroundColor: alpha('rgb(239, 68, 68)', 0.04)
                    },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Delete Greeting
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid rgb(229, 231, 235)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5">Delete Voicemail Greeting?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="mediumParagraph" color="#6B7280">
            Are you sure you want to delete your voicemail greeting? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: 'rgb(229, 231, 235)',
              color: 'rgb(75, 85, 99)',
              '&:hover': {
                borderColor: 'rgb(107, 114, 128)',
                backgroundColor: alpha('rgb(0, 0, 0)', 0.04)
              },
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: '8px',
              backgroundColor: 'rgb(239, 68, 68)',
              '&:hover': {
                backgroundColor: 'rgb(220, 38, 38)',
              },
              textTransform: 'none'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{
            borderRadius: '8px',
            minWidth: '300px'
          }}
        >
          <Typography variant="mediumParagraph" style={{ color: 'white', fontWeight: 500 }}>
            {notification.message}
          </Typography>
        </Alert>
      </Snackbar>
    </StandardContainer>
  );
};
